<script>
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { dynamicTime } from 'shared/helpers/timeHelper';
import { useAdmin } from 'dashboard/composables/useAdmin';
import { splitName } from '@chatwoot/utils';
import countries from 'shared/constants/countries.js';
import { isPhoneE164 } from 'shared/helpers/Validators';
import parsePhoneNumber from 'libphonenumber-js';
import { copyTextToClipboard } from 'shared/helpers/clipboard';
import Avatar from 'next/avatar/Avatar.vue';
import ContactMergeModal from 'dashboard/modules/contact/ContactMergeModal.vue';
import ComposeConversation from 'dashboard/components-next/NewConversation/ComposeConversation.vue';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import NextButton from 'dashboard/components-next/button/Button.vue';
import VoiceCallButton from 'dashboard/components-next/Contacts/VoiceCallButton.vue';
import PhoneNumberInput from 'dashboard/components-next/phonenumberinput/PhoneNumberInput.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import FormInput from 'dashboard/components-next/input/Input.vue';

import {
  isAConversationRoute,
  isAInboxViewRoute,
  getConversationDashboardRoute,
} from '../../../../helper/routeHelpers';
import { emitter } from 'shared/helpers/mitt';

export default {
  components: {
    NextButton,
    Avatar,
    ComposeConversation,
    ContactMergeModal,
    VoiceCallButton,
    PhoneNumberInput,
    ComboBox,
    FormInput,
  },
  props: {
    contact: {
      type: Object,
      default: () => ({}),
    },
    showAvatar: {
      type: Boolean,
      default: true,
    },
    conversationAttributes: {
      type: Object,
      default: () => ({}),
    },
    contactAttributes: {
      type: Object,
      default: () => ({}),
    },
    channelType: {
      type: String,
      default: '',
    },
  },
  emits: ['panelClose'],
  setup() {
    const { isAdmin } = useAdmin();
    return {
      isAdmin,
    };
  },
  data() {
    return {
      showMergeModal: false,
      showDeleteModal: false,
      contactData: {
        firstName: '',
        lastName: '',
        email: '',
        phoneNumber: '',
        additionalAttributes: {
          city: '',
          countryCode: 'IN',
          country: 'India',
          interestedCourse: '',
          educationalQualification: '',
          description: '',
        },
      },
      avatarFile: null,
      avatarUrl: '',
      countries: countries,
    };
  },
  computed: {
    ...mapGetters({ uiFlags: 'contacts/getUIFlags' }),
    contactProfileLink() {
      return `/app/accounts/${this.$route.params.accountId}/contacts/${this.contact.id}`;
    },
    avatarSrc() {
      return this.avatarUrl ? this.avatarUrl : this.contact.thumbnail;
    },
    isUpdating() {
      return this.uiFlags.isUpdating;
    },
    countryOptions() {
      return this.countries.map(({ name, id }) => ({ label: name, value: id }));
    },
    isEmailInvalid() {
      if (!this.contactData.email) return false;
      // Simple email validation regex
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return !emailRegex.test(this.contactData.email);
    },
    isPhoneInvalid() {
      if (!this.contactData.phoneNumber) return false;
      // Check if phone number is in E.164 format
      return !isPhoneE164(this.contactData.phoneNumber);
    },
    canSubmit() {
      // Allow submission if email is empty or valid, and phone is empty or valid
      const emailValid = !this.contactData.email || !this.isEmailInvalid;
      const phoneValid = !this.contactData.phoneNumber || !this.isPhoneInvalid;
      return emailValid && phoneValid;
    },
    // Source from conversation
    sourceDisplay() {
      // Handle both snake_case and camelCase keys (API returns snake_case, but some transforms might use camelCase)
      const source = this.conversationAttributes?.source || this.conversationAttributes?.Source;
      const sourceDisplayValue = this.conversationAttributes?.source_display || this.conversationAttributes?.sourceDisplay;
      const utmSource = this.conversationAttributes?.utm_source || this.conversationAttributes?.utmSource;
      const medium = this.conversationAttributes?.medium || this.conversationAttributes?.Medium;
      const utmMedium = this.conversationAttributes?.utm_medium || this.conversationAttributes?.utmMedium;

      // Debug: Log the values to see what we're getting
      console.log('Source Debug:', { 
        source, 
        sourceDisplayValue, 
        utmSource, 
        medium, 
        utmMedium, 
        allAttrs: this.conversationAttributes 
      });

      // Helper function to normalize and map source
      const mapSourceToDisplay = (sourceValue, mediumValue) => {
        if (!sourceValue) return 'Direct URL';

        const sourceLower = sourceValue.toLowerCase().trim();
        const mediumLower = (mediumValue || '').toLowerCase().trim();

        // Check for paid/ads FIRST (before mapping specific sources)
        // This ensures Google Ads (gclid) shows as "Paid Click" not "Google"
        if (
          sourceLower.includes('paid') ||
          sourceLower.includes('ads') ||
          sourceLower.includes('adwords') ||
          sourceLower.includes('ad') ||
          mediumLower === 'cpc' ||
          mediumLower === 'paid' ||
          mediumLower === 'ppc'
        ) {
          return 'Paid Click';
        }

        // Map specific sources (check exact matches first, then includes)
        if (sourceLower === 'ig' || sourceLower === 'instagram' || sourceLower.includes('instagram') || sourceLower === 'instagram.com') {
          return 'Instagram';
        }
        if (sourceLower === 'fb' || sourceLower === 'facebook' || sourceLower.includes('facebook') || sourceLower === 'facebook.com' || sourceLower === 'fb.com') {
          return 'Facebook';
        }
        if (sourceLower === 'li' || sourceLower === 'linkedin' || sourceLower.includes('linkedin') || sourceLower === 'linkedin.com' || sourceLower.includes('lnkd.in')) {
          return 'LinkedIn';
        }
        if (sourceLower === 'twitter' || sourceLower === 'x' || sourceLower.includes('twitter') || sourceLower === 'twitter.com' || sourceLower === 'x.com' || sourceLower.includes('t.co')) {
          return 'Twitter';
        }
        if (sourceLower === 'youtube' || sourceLower === 'yt' || sourceLower.includes('youtube') || sourceLower === 'youtube.com' || sourceLower.includes('youtu.be')) {
          return 'YouTube';
        }
        if (sourceLower === 'google' || sourceLower.includes('google') || sourceLower === 'google.com') {
          return 'Google';
        }
        if (sourceLower.includes('bing') || sourceLower === 'bing.com' || sourceLower === 'bing') {
          return 'Bing';
        }
        if (sourceLower.includes('chatgpt') || sourceLower.includes('openai') || sourceLower === 'chat.openai.com' || sourceLower === 'chatgpt') {
          return 'ChatGPT';
        }
        if (sourceLower.includes('yahoo') || sourceLower === 'yahoo.com' || sourceLower === 'yahoo') {
          return 'Yahoo';
        }
        if (sourceLower.includes('perplexity') || sourceLower === 'perplexity.ai' || sourceLower === 'perplexity') {
          return 'Perplexity';
        }
        if (sourceLower.includes('brave') || sourceLower === 'brave.com' || sourceLower === 'brave') {
          return 'Brave';
        }

        // If source_display is available and not already mapped, use it
        if (sourceDisplayValue && sourceDisplayValue !== sourceValue) {
          return sourceDisplayValue;
        }

        // If it's "Direct" or empty, return "Direct URL"
        if (sourceValue === 'Direct' || !sourceValue) {
          return 'Direct URL';
        }

        // Return capitalized source as fallback
        return sourceValue.charAt(0).toUpperCase() + sourceValue.slice(1);
      };

      // Priority 1: Use utm_source directly if available (most reliable)
      if (utmSource) {
        return mapSourceToDisplay(utmSource, utmMedium || medium);
      }

      // Priority 2: Use source_display if available, but still map it
      if (sourceDisplayValue) {
        return mapSourceToDisplay(sourceDisplayValue, medium || utmMedium);
      }

      // Priority 3: Use normalized source
      if (source) {
        return mapSourceToDisplay(source, medium || utmMedium);
      }

      // Fallback: Direct URL
      return 'Direct URL';
    },
    landingPage() {
      const pageVisits = (this.conversationAttributes?.page_visits || this.conversationAttributes?.pageVisits || []);
      const firstVisit = pageVisits.length > 0 ? pageVisits[0] : null;

      if (firstVisit) {
        return firstVisit.title || firstVisit.url;
      }

      return (
        this.conversationAttributes?.referer ||
        this.conversationAttributes?.referer_link ||
        this.conversationAttributes?.refererLink ||
        this.conversationAttributes?.referer_url ||
        this.conversationAttributes?.landing_page ||
        this.conversationAttributes?.landed_url ||
        this.contact.additional_attributes?.referer ||
        this.contact.additional_attributes?.referer_link ||
        this.contact.additional_attributes?.refererLink ||
        this.contact.referer ||
        'N/A'
      );
    },
    // IP Address from contact attributes
    ipAddress() {
      return (
        this.contactAttributes?.created_at_ip ||
        this.contactAttributes?.updated_at_ip ||
        'N/A'
      );
    },
    // Delete Modal
    confirmDeleteMessage() {
      return ` ${this.contact.name}?`;
    },
  },
  watch: {
    'contact.id': {
      handler(newId, oldId) {
        if (newId && newId !== oldId) {
          this.initializeContactData();
        }
      },
      immediate: true,
    },
  },
  mounted() {
    this.initializeContactData();
    if (this.contact && this.contact.id) {
      this.$store.dispatch('contacts/fetchContactableInbox', this.contact.id);
    }
  },
  methods: {
    dynamicTime,
    splitName,
    initializeContactData() {
      if (this.contact && this.contact.id) {
        const { firstName, lastName } = splitName(this.contact.name || '');
        const additionalAttrs = this.contact.additional_attributes || {};
        this.contactData = {
          ...this.contact,
          firstName: firstName || '',
          lastName: lastName || '',
          email: this.contact.email || '',
          phoneNumber: this.contact.phone_number || '',
          additionalAttributes: {
            city: additionalAttrs.city || '',
            countryCode: additionalAttrs.country_code || 'IN',
            country: additionalAttrs.country || 'India',
            interestedCourse: additionalAttrs.interested_course || '',
            educationalQualification: additionalAttrs.educational_qualification || '',
            description: additionalAttrs.description || '',
          },
        };
        // Set default country to India if not set
        if (!additionalAttrs.country_code) {
          this.contactData.additionalAttributes.countryCode = 'IN';
          this.contactData.additionalAttributes.country = 'India';
        }
        this.avatarUrl = this.contact.thumbnail || '';
      }
    },
    handleCountrySelection(value) {
      const selectedCountry = this.countries.find(
        option => option.id === value
      );
      this.contactData.additionalAttributes.country =
        selectedCountry?.name || 'India';
      this.contactData.additionalAttributes.countryCode = value || 'IN';
    },
    formatPhoneNumber(phoneNumber) {
      if (!phoneNumber) return null;

      // PhoneNumberInput already returns E.164 format, but let's validate
      if (isPhoneE164(phoneNumber)) {
        return phoneNumber;
      }

      // Try to parse and format the phone number
      try {
        const parsed = parsePhoneNumber(phoneNumber);
        if (parsed && parsed.isValid()) {
          return parsed.format('E.164');
        }
      } catch (e) {
        // If parsing fails, return null to let backend handle it
        return null;
      }

      return null;
    },
    async updateContact() {
      // Validate email
      if (this.contactData.email && this.isEmailInvalid) {
        useAlert(this.$t('CONTACT_FORM.FORM.EMAIL_ADDRESS.ERROR'));
        return;
      }

      // Validate phone number
      if (this.contactData.phoneNumber && this.isPhoneInvalid) {
        useAlert(this.$t('CONTACT_FORM.FORM.PHONE_NUMBER.ERROR'));
        return;
      }

      try {
        // Format phone number to E.164 format
        const formattedPhoneNumber = this.formatPhoneNumber(
          this.contactData.phoneNumber
        );

        // Build contact object for update
        const contactObject = {
          id: this.contact.id,
          name:
            `${this.contactData.firstName} ${this.contactData.lastName}`.trim() ||
            this.contact.name,
          email: (this.contactData.email || '').trim().toLowerCase() || null,
          phone_number: formattedPhoneNumber || null,
          additional_attributes: {
            ...this.contact.additional_attributes,
            city: this.contactData.additionalAttributes?.city || '',
            country_code:
              this.contactData.additionalAttributes?.countryCode || 'IN',
            country: this.contactData.additionalAttributes?.country || 'India',
            interested_course:
              this.contactData.additionalAttributes?.interestedCourse || '',
            educational_qualification:
              this.contactData.additionalAttributes?.educationalQualification || '',
            description:
              this.contactData.additionalAttributes?.description || '',
          },
        };

        await this.$store.dispatch('contacts/update', contactObject);
        await this.$store.dispatch(
          'contacts/fetchContactableInbox',
          this.contact.id
        );
        useAlert(
          this.$t('CONTACTS_LAYOUT.CARD.EDIT_DETAILS_FORM.SUCCESS_MESSAGE')
        );
      } catch (error) {
        // Show actual error message if available
        const errorMessage =
          error?.response?.data?.message ||
          error?.message ||
          this.$t('CONTACTS_LAYOUT.CARD.EDIT_DETAILS_FORM.ERROR_MESSAGE');
        useAlert(errorMessage);
      }
    },
    async handleAvatarUpload({ file, url }) {
      this.avatarFile = file;
      this.avatarUrl = url;

      try {
        const contactObject = {
          id: this.contact.id,
          name:
            `${this.contactData.firstName} ${this.contactData.lastName}`.trim() ||
            this.contact.name,
          email: this.contactData.email || '',
          phone_number: this.contactData.phoneNumber || '',
          avatar: file,
          isFormData: true,
        };
        await this.$store.dispatch('contacts/update', contactObject);
        useAlert(
          this.$t('CONTACTS_LAYOUT.DETAILS.AVATAR.UPLOAD.SUCCESS_MESSAGE')
        );
      } catch {
        useAlert(
          this.$t('CONTACTS_LAYOUT.DETAILS.AVATAR.UPLOAD.ERROR_MESSAGE')
        );
      }
    },
    async handleAvatarDelete() {
      try {
        if (this.contact && this.contact.id) {
          await this.$store.dispatch('contacts/deleteAvatar', this.contact.id);
          useAlert(
            this.$t('CONTACTS_LAYOUT.DETAILS.AVATAR.DELETE.SUCCESS_MESSAGE')
          );
        }
        this.avatarFile = null;
        this.avatarUrl = '';
        this.contactData.thumbnail = null;
      } catch (error) {
        useAlert(
          error.message
            ? error.message
            : this.$t('CONTACTS_LAYOUT.DETAILS.AVATAR.DELETE.ERROR_MESSAGE')
        );
      }
    },
    openComposeConversationModal(toggleFn) {
      toggleFn();
      // Flag to prevent triggering drag n drop,
      // When compose modal is active
      emitter.emit(BUS_EVENTS.NEW_CONVERSATION_MODAL, true);
    },
    closeComposeConversationModal() {
      // Flag to enable drag n drop,
      // When compose modal is closed
      emitter.emit(BUS_EVENTS.NEW_CONVERSATION_MODAL, false);
    },
    toggleDeleteModal() {
      this.showDeleteModal = !this.showDeleteModal;
    },
    confirmDeletion() {
      this.deleteContact(this.contact);
      this.closeDelete();
    },
    closeDelete() {
      this.showDeleteModal = false;
    },
    async deleteContact({ id }) {
      try {
        await this.$store.dispatch('contacts/delete', id);
        this.$emit('panelClose');
        useAlert(this.$t('DELETE_CONTACT.API.SUCCESS_MESSAGE'));

        if (isAConversationRoute(this.$route.name)) {
          this.$router.push({
            name: getConversationDashboardRoute(this.$route.name),
          });
        } else if (isAInboxViewRoute(this.$route.name)) {
          this.$router.push({
            name: 'inbox_view',
          });
        } else if (this.$route.name !== 'contacts_dashboard') {
          this.$router.push({
            name: 'contacts_dashboard',
          });
        }
      } catch (error) {
        useAlert(
          error.message
            ? error.message
            : this.$t('DELETE_CONTACT.API.ERROR_MESSAGE')
        );
      }
    },
    closeMergeModal() {
      this.showMergeModal = false;
    },
    openMergeModal() {
      this.showMergeModal = true;
    },
    async copyContactDetails() {
      const createdAt = this.contact.created_at;
      const formattedDate = createdAt
        ? new Intl.DateTimeFormat('en-GB', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hour12: true,
            timeZone: 'Asia/Kolkata',
          })
            .format(new Date(createdAt * 1000))
            .toLowerCase()
        : '';

      const excelFormat = [
        formattedDate,
        this.contact.name || '',
        this.contact.email || '',
        this.contact.phone_number || '',
        [
          this.contact.additional_attributes?.city,
          this.contact.additional_attributes?.country,
        ]
          .filter(Boolean)
          .join(' - ') || '',
        this.contact.additional_attributes?.description || '',
        this.landingPage || 'N/A',
        this.sourceDisplay || 'Direct URL',
        this.contact.additional_attributes?.interested_course || '',
        this.contact.additional_attributes?.educational_qualification || '',
        this.channelType || 'WebWidget',
      ].join('\t');

      try {
        await copyTextToClipboard(excelFormat);
        useAlert(this.$t('CONTACT_PANEL.COPY_SUCCESSFUL'));
      } catch {
        useAlert('Unable to copy details');
      }
    },
  },
};
</script>

<template>
  <div class="relative items-center w-full p-4">
    <div class="flex flex-col w-full gap-4 text-left rtl:text-right">
      <!-- Avatar and Name Section -->
      <div class="flex flex-col items-start gap-3">
        <Avatar
          v-if="showAvatar"
          :src="avatarSrc || ''"
          :name="contact.name || ''"
          :status="contact.availability_status"
          :size="72"
          allow-upload
          hide-offline-status
          rounded-full
          @upload="handleAvatarUpload"
          @delete="handleAvatarDelete"
        />
        <div class="flex flex-col gap-1">
          <div class="flex items-center w-full min-w-0 gap-3">
            <h3
              class="flex-shrink max-w-full min-w-0 my-0 text-base font-medium capitalize break-words text-n-slate-12"
            >
              {{ contact.name }}
            </h3>
            <div class="flex flex-row items-center gap-2">
              <span
                v-if="contact.created_at"
                v-tooltip.left="
                  `${$t('CONTACT_PANEL.CREATED_AT_LABEL')} ${dynamicTime(
                    contact.created_at
                  )}`
                "
                class="i-lucide-info text-sm text-n-slate-10"
              />
              <a
                :href="contactProfileLink"
                target="_blank"
                rel="noopener nofollow noreferrer"
                class="leading-3"
              >
                <span class="i-lucide-external-link text-sm text-n-slate-10" />
              </a>
            </div>
          </div>
          <span
            v-if="contact.identifier"
            class="inline-flex items-center gap-1 text-sm text-n-slate-11"
          >
            <span class="i-ph-user-gear text-n-slate-10 size-4" />
            {{ contact.identifier }}
          </span>
        </div>
      </div>

      <!-- Action Buttons -->
      <div class="flex items-center w-full gap-2">
        <ComposeConversation
          :contact-id="String(contact.id)"
          is-modal
          @close="closeComposeConversationModal"
        >
          <template #trigger="{ toggle }">
            <NextButton
              v-tooltip.top-end="$t('CONTACT_PANEL.NEW_MESSAGE')"
              icon="i-ph-chat-circle-dots"
              slate
              faded
              sm
              @click="openComposeConversationModal(toggle)"
            />
          </template>
        </ComposeConversation>
        <VoiceCallButton
          :phone="contact.phone_number"
          icon="i-ri-phone-fill"
          size="sm"
          :tooltip-label="$t('CONTACT_PANEL.CALL')"
          slate
          faded
        />
        <NextButton
          v-tooltip.top-end="$t('CONTACT_PANEL.MERGE_CONTACT')"
          icon="i-ph-arrows-merge"
          slate
          faded
          sm
          :disabled="uiFlags.isMerging"
          @click="openMergeModal"
        />
        <NextButton
          v-if="isAdmin"
          v-tooltip.top-end="$t('DELETE_CONTACT.BUTTON_LABEL')"
          icon="i-ph-trash"
          slate
          faded
          sm
          ruby
          :disabled="uiFlags.isDeleting"
          @click="toggleDeleteModal"
        />
        <NextButton
          v-tooltip.top-end="$t('CONTACT_PANEL.COPY_DETAILS')"
          icon="i-ph-copy"
          slate
          faded
          sm
          @click="copyContactDetails"
        />
      </div>

      <!-- Inline Editable Form -->
      <div v-if="contact.id" class="flex flex-col gap-3 w-full">
        <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <!-- First Name & Last Name -->
          <div class="flex flex-col gap-1">
            <label class="text-xs text-n-slate-11">{{
              $t('CONTACT_FORM.FORM.FIRST_NAME.LABEL')
            }}</label>
            <FormInput
              v-model="contactData.firstName"
              :placeholder="$t('CONTACT_FORM.FORM.FIRST_NAME.PLACEHOLDER')"
              custom-input-class="h-8 !pt-1 !pb-1 [&:not(.error,.focus)]:!outline-transparent"
              class="w-full"
            />
          </div>
          <div class="flex flex-col gap-1">
            <label class="text-xs text-n-slate-11">{{
              $t('CONTACT_FORM.FORM.LAST_NAME.LABEL')
            }}</label>
            <FormInput
              v-model="contactData.lastName"
              :placeholder="$t('CONTACT_FORM.FORM.LAST_NAME.PLACEHOLDER')"
              custom-input-class="h-8 !pt-1 !pb-1 [&:not(.error,.focus)]:!outline-transparent"
              class="w-full"
            />
          </div>

          <!-- Email & Source -->
          <div class="flex flex-col gap-1">
            <label class="text-xs text-n-slate-11">{{
              $t('CONTACT_FORM.FORM.EMAIL_ADDRESS.LABEL')
            }}</label>
            <FormInput
              v-model="contactData.email"
              :placeholder="$t('CONTACT_FORM.FORM.EMAIL_ADDRESS.PLACEHOLDER')"
              custom-input-class="h-8 !pt-1 !pb-1 [&:not(.error,.focus)]:!outline-transparent"
              :message-type="isEmailInvalid ? 'error' : 'info'"
              class="w-full"
            />
            <span v-if="isEmailInvalid" class="text-xs text-n-ruby-9">
              {{ $t('CONTACT_FORM.FORM.EMAIL_ADDRESS.ERROR') }}
            </span>
          </div>
          <div class="flex flex-col gap-1">
            <label class="text-xs text-n-slate-11">{{
              $t('CONTACT_PANEL.SOURCE')
            }}</label>
            <div
              class="h-8 px-2 text-sm border border-n-weak rounded bg-n-alpha-black2 text-n-slate-11 flex items-center"
            >
              {{ sourceDisplay }}
            </div>
          </div>

          <!-- Select Branch & Current City -->
          <div class="flex flex-col gap-1">
            <label class="text-xs text-n-slate-11">{{
              $t('CONTACT_FORM.FORM.CITY.LABEL')
            }}</label>
            <FormInput
              v-model="contactData.additionalAttributes.city"
              :placeholder="$t('CONTACT_FORM.FORM.CITY.PLACEHOLDER')"
              custom-input-class="h-8 !pt-1 !pb-1 [&:not(.error,.focus)]:!outline-transparent"
              class="w-full"
            />
          </div>
          <div class="flex flex-col gap-1">
            <label class="text-xs text-n-slate-11">{{
              $t('CONTACT_FORM.FORM.BIO.LABEL')
            }}</label>
            <FormInput
              v-model="contactData.additionalAttributes.description"
              :placeholder="$t('CONTACT_FORM.FORM.BIO.PLACEHOLDER')"
              custom-input-class="h-8 !pt-1 !pb-1 [&:not(.error,.focus)]:!outline-transparent"
              class="w-full"
            />
          </div>

          <!-- Country & Interested In -->
          <div class="flex flex-col gap-1">
            <label class="text-xs text-n-slate-11">{{
              $t('CONTACT_FORM.FORM.COUNTRY.LABEL')
            }}</label>
            <ComboBox
              v-model="contactData.additionalAttributes.countryCode"
              :options="countryOptions"
              :placeholder="$t('CONTACT_FORM.FORM.COUNTRY.SELECT_COUNTRY')"
              class="[&>div>button]:h-8 [&>div>button]:bg-n-alpha-black2 [&>div>button:not(.focused)]:!outline-transparent"
              @update:model-value="handleCountrySelection"
            />
          </div>
          <div class="flex flex-col gap-1">
            <label class="text-xs text-n-slate-11">{{
              $t('CONTACT_FORM.FORM.INTERESTED_COURSE.LABEL')
            }}</label>
            <FormInput
              v-model="contactData.additionalAttributes.interestedCourse"
              :placeholder="
                $t('CONTACT_FORM.FORM.INTERESTED_COURSE.PLACEHOLDER')
              "
              custom-input-class="h-8 !pt-1 !pb-1 [&:not(.error,.focus)]:!outline-transparent"
              class="w-full"
            />
          </div>
          <div class="flex flex-col gap-1">
            <label class="text-xs text-n-slate-11">{{
              $t('CONTACT_FORM.FORM.EDUCATIONAL_QUALIFICATION.LABEL')
            }}</label>
            <FormInput
              v-model="contactData.additionalAttributes.educationalQualification"
              :placeholder="
                $t('CONTACT_FORM.FORM.EDUCATIONAL_QUALIFICATION.PLACEHOLDER')
              "
              custom-input-class="h-8 !pt-1 !pb-1 [&:not(.error,.focus)]:!outline-transparent"
              class="w-full"
            />
          </div>
        </div>

        <!-- Mobile Number - Full Width -->
        <div class="flex flex-col gap-1 pt-3 border-t border-n-weak w-full">
          <label class="text-xs text-n-slate-11">{{
            $t('CONTACT_FORM.FORM.PHONE_NUMBER.LABEL')
          }}</label>
          <div class="w-full">
            <PhoneNumberInput
              v-model="contactData.phoneNumber"
              :placeholder="$t('CONTACT_FORM.FORM.PHONE_NUMBER.PLACEHOLDER')"
              show-border
            />
          </div>
        </div>

        <!-- Landing Page - Full Width -->
        <div class="flex flex-col gap-1 pt-3 border-t border-n-weak w-full">
          <label class="text-xs text-n-slate-11">{{
            $t('CONTACT_PANEL.LANDING_PAGE')
          }}</label>
          <div
            class="h-8 px-2 text-sm border border-n-weak rounded bg-n-alpha-black2 text-n-slate-11 flex items-center overflow-hidden whitespace-nowrap text-ellipsis"
            :title="landingPage"
          >
            {{ landingPage }}
          </div>
        </div>

        <!-- Update Button -->
        <div class="flex items-start w-full gap-2 pt-2">
          <NextButton
            :label="$t('CONTACTS_LAYOUT.CARD.EDIT_DETAILS_FORM.UPDATE_BUTTON')"
            size="sm"
            :is-loading="isUpdating"
            :disabled="isUpdating || !canSubmit"
            @click="updateContact"
          />
        </div>
      </div>

      <!-- Modals -->
      <ContactMergeModal
        v-if="showMergeModal"
        :primary-contact="contact"
        :show="showMergeModal"
        @close="closeMergeModal"
      />
    </div>
    <woot-delete-modal
      v-if="showDeleteModal"
      v-model:show="showDeleteModal"
      :on-close="closeDelete"
      :on-confirm="confirmDeletion"
      :title="$t('DELETE_CONTACT.CONFIRM.TITLE')"
      :message="$t('DELETE_CONTACT.CONFIRM.MESSAGE')"
      :message-value="confirmDeleteMessage"
      :confirm-text="$t('DELETE_CONTACT.CONFIRM.YES')"
      :reject-text="$t('DELETE_CONTACT.CONFIRM.NO')"
    />
  </div>
</template>
